import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { findAum, findStrategy, processData, signalsData } from "../../component/Methods";
import MonitorScreenCalculation from "./MonitorScreenCalculation";
import { setCalculatedMonitorScreen } from "../../Redux/Reducers/marketDataSlice";
import { updateLoading, updateLoadingMsg } from "../../Redux/Reducers/uiSlice";

export const usePortfolioCalculations = ({
  funds,
  lots,
  accountingData,
  strategies,
  livePrices,
  trades,
  markets,
  isStrategist,
  managers,
  signalsPerformance,
}) => {
  const dispatch = useDispatch();

  // Memoize derived values
  const fund = useMemo(() => accountingData[0]?.funds, [accountingData]);

  // Filter data calculation
  const filterData = useCallback(
    (fundVal, StrategyVal, StrategeyTypename) => {
      let processedFundVal = fundVal;
      let processedStrategyVal = StrategyVal;

      if (fundVal === "ALL" || StrategyVal === "ALL") {
        processedFundVal = undefined;
        if (!isStrategist) processedStrategyVal = undefined;
      }

      const fundObject = funds?.find((obj) => obj.abbreviation === processedFundVal);
      if (fundObject) processedFundVal = fundObject.fundId;

      const filteredData = MonitorScreenCalculation({
        selectedFund: processedFundVal,
        selectedStrategy: processedStrategyVal,
        selectedSide: StrategeyTypename,
        lots,
        accountingData,
        funds,
        strategies,
        marketData: livePrices,
        trades,
        isStrategist,
        markets,
      });

      if (filteredData) {
        dispatch(setCalculatedMonitorScreen(filteredData));
      }

      return filteredData;
    },
    [funds, isStrategist, lots, accountingData, strategies, livePrices, trades, markets, dispatch]
  );

  // Signal calculations
  const calculateSignals = useCallback(
    (fnd, stra, typ) => {
      if (!fnd || !stra) {
        return { Long: 0, Short: 0 };
      }

      if (stra === undefined || stra === "ALL" || stra === "") {
        return { Long: 0, Short: 0 };
      }

      let matchingStrategies;
      if (typ === "Both") {
        matchingStrategies = strategies?.find((strategy) => strategy.name === stra);
      } else if (typ === "Long" || typ === "Short") {
        matchingStrategies = strategies?.find((strategy) =>
          strategy.name.includes(`${stra}_${typ}`)
        );
      }

      if (matchingStrategies) {
        const signalsCalc = signalsData(signalsPerformance, strategies, matchingStrategies.strategyId);

        const positiveValues = signalsCalc
          .filter((signal) => signal.value >= 0)
          .reduce((sum, signal) => sum + signal.PercentChangeDay * signal.value, 0);

        const negativeValues = signalsCalc
          .filter((signal) => signal.value < 0)
          .reduce((sum, signal) => sum + signal.PercentChangeDay * signal.value, 0);

        return {
          Long: positiveValues,
          Short: negativeValues,
        };
      }

      return { Long: 0, Short: 0 };
    },
    [strategies, signalsPerformance]
  );

  // Main calculation function
  const calculatePositions = useCallback(
    (fnd, stra, typ, setFilteredValues, setTotals) => {
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Calculating Positions"));

      // Clear caches periodically to prevent memory leaks
      if (Math.random() < 0.01) {
        if (typeof window !== "undefined") {
          if (window.priceCache) window.priceCache.clear();
          if (window.deltaCache) window.deltaCache.clear();
          if (window.strategyCache) window.strategyCache.clear();
          if (window.fundCache) window.fundCache.clear();
        }
      }

      // Use setTimeout to prevent blocking the UI
      setTimeout(() => {
        const monitorScreenValuesObj = filterData(fnd, stra, typ);
        const { Long, Short } = calculateSignals(fnd, stra, typ);

        if (!monitorScreenValuesObj) {
          setFilteredValues([]);
          setTotals([]);
          dispatch(updateLoading(false));
          dispatch(updateLoadingMsg(""));
          return;
        }

        let aum;
        const result = monitorScreenValuesObj.reduce((acc, obj) => {
          if (obj.fund === "All") {
            aum = accountingData[0]?.funds.reduce((total, obj) => total + obj.aum, 0);
          } else {
            if (obj?.strategyName !== "All") {
              const selectedFund = accountingData[0]?.funds.find((fund) => fund.fund === obj.fund);
              if (selectedFund) {
                aum = selectedFund?.strategyAUMs[obj?.strategyName];
              } else {
                aum = findAum(obj.fund, accountingData[0]?.funds);
              }
            } else {
              aum = findAum(obj.fund, accountingData[0]?.funds);
            }
          }

          if (obj.deltaAdjustedShareCount > 0) {
            acc.positiveAUMTotal = (acc.positiveAUMTotal || 0) + parseFloat(obj.size);
            acc.positivePLTotal = (acc.positivePLTotal || 0) + parseFloat(obj.pL);
          } else if (obj.deltaAdjustedShareCount < 0) {
            acc.negativeAUMTotal = (acc.negativeAUMTotal || 0) + parseFloat(obj.size);
            acc.negativePLTotal = (acc.negativePLTotal || 0) + parseFloat(obj.pL);
          }
          return acc;
        }, {});

        const totalPL = result.positivePLTotal + result.negativePLTotal;
        const totalAUM = result.positiveAUMTotal + result.negativeAUMTotal;
        const totalsPcm =
          (result.positivePLTotal / ((result.positiveAUMTotal * aum) / 100)) * 100 +
          (result.negativePLTotal / (((result.negativeAUMTotal * aum) / 100) * -1)) * 100;

        const newArray = [
          {
            name: "LONG",
            totalsAUM: result.positiveAUMTotal,
            totalsPl: result.positivePLTotal,
            totalSignals: Long,
            totalsPcm:
              isNaN(result.positivePLTotal) || result.positivePLTotal === 0
                ? "-"
                : (result.positivePLTotal / ((result.positiveAUMTotal * aum) / 100)) * 100,
          },
          {
            name: "SHORT",
            totalsAUM: result.negativeAUMTotal,
            totalsPl: result.negativePLTotal,
            totalsPcm:
              isNaN(result.negativePLTotal) || result.negativePLTotal === 0
                ? "-"
                : (result.negativePLTotal / ((result.negativeAUMTotal * aum) / 100)) * 100 * -1,
            totalSignals: Short,
          },
          {
            name: "TOTALS",
            totalsAUM: isNaN(totalAUM) ? "-" : totalAUM,
            totalsPl: isNaN(totalPL) ? "-" : totalPL,
            totalsPcm: isNaN(totalsPcm) ? "-" : totalsPcm,
            totalSignals: Short + Long,
          },
        ];

        setFilteredValues(monitorScreenValuesObj);
        setTotals(newArray);

        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
      }, 0);
    },
    [
      filterData,
      calculateSignals,
      accountingData,
      dispatch,
    ]
  );

  return {
    fund,
    filterData,
    calculateSignals,
    calculatePositions,
  };
};
